import type { RealEstateContact } from "../../@types/sharedSchema";

/**
 * THE ONE definition, in the browser, of "when was this contact last bulk-messaged".
 *
 * Two rails stamp it, into two different fields:
 *  - a send through the Chrome extension → `lastBulkMessageReceived.sentTime`;
 *  - an official-WhatsApp campaign (`recordPairSend` on the server) → `lastBulkMessageReceivedTime`.
 *
 * The answer is the LATER of the two — the same `GREATEST(…)` the server's mirror row carries
 * as `lastBulkMessageSentTime`, which is what the CRM list shows and filters on.
 *
 * Why this exists (Trello #237): the CRM side panel post-processes the document and
 * `postProcessContact` used to OVERWRITE `lastBulkMessageReceivedTime` with the extension's
 * stamp, while the full contact page renders the raw document. A contact reached only by a
 * campaign therefore read "אין תיעוד עדיין" under מגע אחרון in the panel and "לפני 6 ימים ·
 * דיוור" on the full page — the full page was right. Every reader now goes through here, so
 * a raw document and a post-processed one give the same answer.
 *
 * Returns the stored value of the later stamp (an ISO string as written; the listener's
 * converter may hand a Date), or undefined when neither rail ever sent.
 */
export function latestBulkMessageTime(
  contact: Pick<RealEstateContact, "lastBulkMessageReceived" | "lastBulkMessageReceivedTime"> | null | undefined
): string | undefined {
  const extensionSend = contact?.lastBulkMessageReceived?.sentTime;
  const campaignSend = contact?.lastBulkMessageReceivedTime;
  const extensionMs = toMs(extensionSend);
  const campaignMs = toMs(campaignSend);
  if (extensionMs === null && campaignMs === null) return undefined;
  if (campaignMs === null) return extensionSend;
  if (extensionMs === null) return campaignSend;
  return campaignMs >= extensionMs ? campaignSend : extensionSend;
}

/** Tolerant: an ISO string or a Date; anything unparseable counts as "no stamp". */
function toMs(value: unknown): number | null {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value as string);
  const ms = parsed.getTime();
  return Number.isNaN(ms) ? null : ms;
}
